/**
 * @usable/office-embed — host-agnostic Usable Chat embed bridge, Keycloak auth,
 * the chat-embed hook, and local chat-history persistence for Office add-ins.
 *
 * The companion `auth-dialog.html` ships alongside this source and is wired into
 * the build by @usable/build-config (resolve via `@usable/office-embed/auth-dialog.html`).
 */

export { UsableChatEmbed } from "./embed-sdk";
export type {
  ParentToolSchema,
  ToolCallPayload,
  ToolCallHandler,
  ContextEntry,
  GeneratedImagePayload,
  StagedFileEntry,
  StagedFileBytes,
  StagedDataApi,
  AttachmentEntry,
  AttachmentBytes,
  AttachmentsApi,
  WorkspaceFileBytes,
  WorkspaceFilesApi,
  UsableChatEmbedOptions,
  ExportedMessage,
  ConversationMessagesUpsertMode,
  StatelessConversationPayload,
  StatelessMessageCreatedPayload,
  ConversationRenamedPayload,
  ConversationMessagesUpsertPayload,
  ConversationMessagesUpsertedPayload,
} from "./embed-sdk";

export { useAuth } from "./use-auth";
export type { AuthState, AuthResult } from "./use-auth";

export { useChatEmbed } from "./use-chat-embed";
export type { ChatEmbedConfig, ChatEmbedApi, HostContextProvider } from "./use-chat-embed";

export { capText } from "./context-format";

export {
  createLocalFolderProvider,
  getActiveLocalFolderNotice,
  readActiveFolderFileAsDataUrl,
  normalizeFileList,
  readEntryText,
  nearestPaths,
  MAX_FOLDER_ENTRIES,
  MAX_READABLE_FILE_BYTES,
  MAX_READ_CHARS,
} from "./local-folder";
export type { LocalFolderProvider, LocalFolderEntry } from "./local-folder";

export { deliverImageAttachment } from "./image-attachment";
export type { ToolCallCapabilities, ImageAttachmentRule } from "./image-attachment";

export {
  executeToolCall,
  serializeExecution,
  describeOfficeError,
  DEFAULT_TOOL_TIMEOUT_MS,
  LONG_TOOL_TIMEOUT_MS,
  __resetToolExecutionForTests,
} from "./tool-execution";
export type { SerializeOptions, ExecuteOptions } from "./tool-execution";

export { createGraphAuth, GraphConsentRequired, graphConsentToolResult } from "./graph-auth";
export type {
  GraphAuth,
  GraphAuthConfig,
  AcquireGraphTokenOptions,
  InteractionRequiredResult,
} from "./graph-auth";

export { createChatHistoryStore, DEFAULT_CONVERSATION_TITLE } from "./history-store";
export type {
  ChatHistoryStore,
  ConversationRecord,
  MessageRecord,
  UpsertConversationInput,
  AppendMessageInput,
} from "./history-store";
